import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldAlt, faPlay, faStop, faSpinner, faDownload,
  faKey, faSync, faCopy, faCheckCircle, faTimesCircle,
  faNetworkWired, faRobot, faTrash, faChevronDown, faChevronRight, faRedo
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import proxyService from '../../services/proxyService';
import authProfileService from '../../services/authProfileService';
import webTargetService from '../../services/webTargetService';
import agentService from '../../services/agentService';
import MatchReplaceEditor from './MatchReplaceEditor';
import ProxyCookieManager from './ProxyCookieManager';
import CloudflareSolver from './CloudflareSolver';

const DEFAULT_DONT_RECORD = ['image/*', 'font/*', 'text/css', 'application/javascript', 'application/octet-stream'];

// Burp-style automatic response transforms (applied to HTML responses). Keys match
// ProxySession.proxyConfig.responseModifications / WebTarget.proxySettings.responseModifications.
const RESPONSE_MOD_OPTIONS = [
  { key: 'unhideHiddenFields', label: 'Unhide hidden form fields', hint: 'type="hidden" → type="text" so hidden inputs are visible/editable' },
  { key: 'enableDisabledFields', label: 'Enable disabled form fields', hint: 'Removes the disabled attribute from inputs/buttons' },
  { key: 'removeInputLengthLimits', label: 'Remove input length limits', hint: 'Strips maxlength so you can submit oversized values' },
  { key: 'removeJsValidation', label: 'Remove JS form validation', hint: 'Strips required, pattern and onsubmit handlers' },
  { key: 'removeAllJavascript', label: 'Remove all JavaScript', hint: 'Strips <script>, on* handlers and javascript: hrefs' },
  { key: 'removeObjectTags', label: 'Remove <object>/<embed> tags', hint: 'Strips <object>, <embed> and <applet> elements' },
  { key: 'convertHttpsToHttp', label: 'Convert HTTPS links to HTTP', hint: 'Rewrites https:// → http:// in the response body' },
  { key: 'removeSecureCookieFlag', label: 'Remove Secure flag from cookies', hint: 'Strips "; Secure" from Set-Cookie so cookies flow over HTTP' }
];

const ProxyControlPanel = ({ webTarget, isDarkMode, onRefresh }) => {
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cookieRefreshTrigger, setCookieRefreshTrigger] = useState(0);
  const [capturedAuth, setCapturedAuth] = useState([]);
  const [config, setConfig] = useState({
    port: 8080,
    listenAddress: '0.0.0.0',
    interceptEnabled: true,
    scopeInclude: '',
    scopeExclude: '',
    routeViaAgent: false,
    selectedAgentId: null,
    agentTlsSkipVerify: true,
    decompressResponses: true,
    useTlsFingerprintSpoofing: false,
    tlsBrowserProfile: 'chrome',
    useHttp2: true,
    matchReplaceRules: [],
    responseModifications: {
      unhideHiddenFields: false,
      enableDisabledFields: false,
      removeInputLengthLimits: false,
      removeJsValidation: false,
      removeAllJavascript: false,
      removeObjectTags: false,
      convertHttpsToHttp: false,
      removeSecureCookieFlag: false
    },
    tlsPassThrough: '',
    dontRecordContentTypes: DEFAULT_DONT_RECORD.join('\n'),
    dontRecordUrlPatterns: ''
  });
  const [settingsDirty, setSettingsDirty] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [onlineAgents, setOnlineAgents] = useState([]);
  const [expandedSections, setExpandedSections] = useState({});
  const [pendingChanges, setPendingChanges] = useState([]);
  const [caFingerprint, setCaFingerprint] = useState(null);

  const toggleSection = (key) => setExpandedSections(p => ({ ...p, [key]: !p[key] }));

  useEffect(() => {
    if (webTarget?._id) {
      loadSessions();
      webTargetService.getProxyCaInfo(webTarget._id)
        .then(info => setCaFingerprint(info?.fingerprint || null))
        .catch(() => {});
      const updates = {};
      if (webTarget.scope) {
        updates.scopeInclude = (webTarget.scope.includePatterns || []).join('\n');
        updates.scopeExclude = (webTarget.scope.excludePatterns || []).join('\n');
      }
      if (webTarget.proxySettings) {
        updates.listenAddress = webTarget.proxySettings.listenAddress || '0.0.0.0';
        updates.port = webTarget.proxySettings.listenPort || 8080;
        updates.interceptEnabled = webTarget.proxySettings.interceptEnabled !== false;
        updates.decompressResponses = webTarget.proxySettings.decompressResponses !== false;
        if (webTarget.proxySettings.matchReplaceRules?.length) {
          updates.matchReplaceRules = webTarget.proxySettings.matchReplaceRules;
        }
        if (webTarget.proxySettings.responseModifications) {
          updates.responseModifications = {
            ...config.responseModifications,
            ...webTarget.proxySettings.responseModifications
          };
        }
        if (webTarget.proxySettings.tlsPassThrough?.length) {
          updates.tlsPassThrough = webTarget.proxySettings.tlsPassThrough.join('\n');
        }
        if (webTarget.proxySettings.dontRecordContentTypes?.length) {
          updates.dontRecordContentTypes = webTarget.proxySettings.dontRecordContentTypes.join('\n');
        }
        if (webTarget.proxySettings.dontRecordUrlPatterns?.length) {
          updates.dontRecordUrlPatterns = webTarget.proxySettings.dontRecordUrlPatterns.join('\n');
        }
      }
      if (webTarget.routeViaAgent) {
        updates.routeViaAgent = webTarget.routeViaAgent.enabled || false;
        updates.selectedAgentId = webTarget.routeViaAgent.agentId || null;
        updates.agentTlsSkipVerify = webTarget.routeViaAgent.tlsSkipVerify !== false;
      }
      if (Object.keys(updates).length) setConfig(p => ({ ...p, ...updates }));
      loadOnlineAgents();
    }
  }, [webTarget]);

  const loadOnlineAgents = async () => {
    try {
      const agents = await agentService.getConnectedAgents();
      setOnlineAgents(agents);
    } catch {}
  };

  const handleSaveSettings = async () => {
    setSettingsSaving(true);
    try {
      await webTargetService.update(webTarget._id, {
        proxySettings: {
          listenAddress: config.listenAddress,
          listenPort: config.port,
          interceptEnabled: config.interceptEnabled,
          decompressResponses: config.decompressResponses,
          matchReplaceRules: config.matchReplaceRules,
          responseModifications: config.responseModifications,
          tlsPassThrough: config.tlsPassThrough ? config.tlsPassThrough.split('\n').filter(Boolean) : [],
          dontRecordContentTypes: config.dontRecordContentTypes ? config.dontRecordContentTypes.split('\n').filter(Boolean) : [],
          dontRecordUrlPatterns: config.dontRecordUrlPatterns ? config.dontRecordUrlPatterns.split('\n').map(s => s.trim()).filter(Boolean) : []
        },
        scope: {
          includePatterns: config.scopeInclude ? config.scopeInclude.split('\n').filter(Boolean) : [],
          excludePatterns: config.scopeExclude ? config.scopeExclude.split('\n').filter(Boolean) : []
        },
        routeViaAgent: {
          enabled: config.routeViaAgent,
          agentId: config.selectedAgentId || null,
          mode: 'http_relay',
          tlsSkipVerify: config.agentTlsSkipVerify
        }
      });
      // Live-apply scope + match/replace + response modifications to a running session
      // so changes take effect immediately without a restart.
      if (activeSession?._id) {
        try {
          await proxyService.updateScope(activeSession._id, {
            includeHosts: config.scopeInclude ? config.scopeInclude.split('\n').map(s => s.trim()).filter(Boolean) : [],
            excludeHosts: config.scopeExclude ? config.scopeExclude.split('\n').map(s => s.trim()).filter(Boolean) : []
          });
          await proxyService.updateModifications(activeSession._id, {
            matchReplaceRules: config.matchReplaceRules,
            responseModifications: config.responseModifications,
            dontRecordUrlPatterns: config.dontRecordUrlPatterns ? config.dontRecordUrlPatterns.split('\n').map(s => s.trim()).filter(Boolean) : []
          });
          toast.success('Applied live to the running proxy');
        } catch (e) {
          console.error('Live-apply failed:', e);
          toast.warning('Saved, but live-apply to the running proxy failed');
        }
      }
      setSettingsDirty(false);
    } catch (err) {
      console.error('Save settings error:', err);
    } finally {
      setSettingsSaving(false);
    }
  };

  const loadSessions = async () => {
    try {
      const data = await proxyService.getAll({ webTargetId: webTarget._id });
      setSessions(data);
      const active = data.find(s => s.status === 'running');
      if (active) {
        // Check if the proxy is ACTUALLY running in-memory (not just DB status)
        if (active.isActive === false) {
          // Ghost session — DB says running but backend has no in-memory server.
          // This happens after PM2/backend restarts. Auto-fix: mark as stopped.
          console.warn('[Proxy] Stale session detected — proxy is not actually running, cleaning up.');
          try {
            await proxyService.stop(active._id);
          } catch {}
          setActiveSession(null);
          loadSessions(); // reload to reflect corrected status
          return;
        }
        setActiveSession(active);
        loadCapturedAuth(active._id);
      }
    } catch {}
  };

  const loadCapturedAuth = async (sessionId) => {
    try {
      const data = await proxyService.getCapturedAuth(sessionId);
      setCapturedAuth(data);
    } catch {}
  };

  const loadPendingChanges = async (sessionId) => {
    try {
      const session = await proxyService.getById(sessionId);
      setPendingChanges(session.pendingConfigChanges || []);
    } catch {}
  };

  useEffect(() => {
    if (activeSession?._id) {
      const interval = setInterval(() => {
        loadPendingChanges(activeSession._id);
      }, 3000); // Check every 3 seconds
      return () => clearInterval(interval);
    }
  }, [activeSession]);

  const _buildProxyConfig = () => ({
    listenPort: config.port,
    listenAddress: config.listenAddress,
    interceptEnabled: config.interceptEnabled,
    decompressResponses: config.decompressResponses,
    useTlsFingerprintSpoofing: config.useTlsFingerprintSpoofing,
    tlsBrowserProfile: config.tlsBrowserProfile,
    useHttp2: config.useHttp2,
    matchReplaceRules: config.matchReplaceRules,
    tlsPassThrough: config.tlsPassThrough ? config.tlsPassThrough.split('\n').filter(Boolean) : [],
    dontRecordContentTypes: config.dontRecordContentTypes ? config.dontRecordContentTypes.split('\n').filter(Boolean) : [],
    scope: {
      includeHosts: config.scopeInclude ? config.scopeInclude.split('\n').filter(Boolean) : [],
      excludeHosts: config.scopeExclude ? config.scopeExclude.split('\n').filter(Boolean) : []
    }
  });

  const _doStart = async (overridePort) => {
    const proxyConfig = _buildProxyConfig();
    if (overridePort) proxyConfig.listenPort = overridePort;

    const session = await proxyService.create({
      webTargetId: webTarget._id,
      projectId: webTarget.project,
      name: `Proxy ${new Date().toLocaleTimeString()}`,
      proxyConfig
    });
    await proxyService.start(session._id);
    setActiveSession({ ...session, status: 'running', port: overridePort || config.port });
    // Refresh CA fingerprint — it may have been bootstrapped on first start
    try {
      const info = await webTargetService.getProxyCaInfo(webTarget._id);
      setCaFingerprint(info?.fingerprint || null);
    } catch {}
    loadSessions();
    onRefresh?.();
    if (overridePort && overridePort !== config.port) {
      setConfig(p => ({ ...p, port: overridePort }));
      toast.info(`Proxy started on port ${overridePort} (original port ${config.port} was occupied).`);
    }
  };

  const handleStart = async () => {
    setLoading(true);
    try {
      await _doStart();
    } catch (err) {
      const conflict = err.response?.data?.portConflict;
      if (conflict?.type === 'same_tenant') {
        // Same tenant — offer to stop the other project's proxy and start here
        const ok = window.confirm(
          `Port ${conflict.port} is already in use by the proxy for "${conflict.webTargetName}" in project "${conflict.projectName}".\n\nStop that proxy and start it here instead?`
        );
        if (ok) {
          try {
            await proxyService.stop(conflict.runningSessionId);
            await new Promise(r => setTimeout(r, 500));
            await _doStart();
          } catch (err2) {
            toast.error('Failed to switch proxy: ' + (err2.response?.data?.error || err2.message));
          }
        }
      } else if (conflict?.type === 'other_tenant') {
        // Different tenant — suggest alternate port
        const suggested = conflict.suggestedPort || (config.port + 1);
        const ok = window.confirm(
          `Port ${conflict.port} is already in use by another tenant's proxy.\n\nStart on port ${suggested} instead?`
        );
        if (ok) {
          try {
            await _doStart(suggested);
          } catch (err2) {
            toast.error('Failed to start on port ' + suggested + ': ' + (err2.response?.data?.error || err2.message));
          }
        }
      } else if (conflict?.type === 'os') {
        toast.error(`Port ${conflict.port} is already in use by another process on this system. Choose a different port.`);
      } else {
        toast.error('Proxy start failed: ' + (err.response?.data?.error || err.message));
      }
    } finally {
      setLoading(false);
    }
  };

  const markDirty = () => setSettingsDirty(true);

  const responseModCount = Object.values(config.responseModifications || {}).filter(Boolean).length;

  // "Allow all hosts" == the include list contains the "*" wildcard (backend treats
  // it as match-everything). Toggling on replaces the list with "*"; off clears it.
  const allowAllHosts = config.scopeInclude.split('\n').map(s => s.trim()).includes('*');
  const toggleAllowAllHosts = (checked) => {
    setConfig(p => {
      if (checked) return { ...p, scopeInclude: '*' };
      const lines = p.scopeInclude.split('\n').map(s => s.trim()).filter(Boolean).filter(l => l !== '*');
      return { ...p, scopeInclude: lines.join('\n') };
    });
    markDirty();
  };

  const handleStop = async () => {
    if (!activeSession) return;
    setLoading(true);
    try {
      await proxyService.stop(activeSession._id);
      setActiveSession(null);
      loadSessions();
      onRefresh?.();
    } catch (err) {
      console.error('Proxy stop error:', err);
      alert(`Failed to stop proxy: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRestart = async () => {
    if (!activeSession) return;
    setLoading(true);
    try {
      const result = await proxyService.restart(activeSession._id);
      setActiveSession({ ...activeSession, status: 'running', port: result.port });
      // Refresh CA fingerprint
      try {
        const info = await webTargetService.getProxyCaInfo(webTarget._id);
        setCaFingerprint(info?.fingerprint || null);
      } catch {}
      loadSessions();
      onRefresh?.();
    } catch (err) {
      console.error('Proxy restart error:', err);
      toast.error('Failed to restart proxy: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCA = async () => {
    if (!webTarget?._id) return;
    try {
      // Target-level CA — works even without a running session, and always returns
      // the fixed CA (never a session-ephemeral one).
      const cert = await webTargetService.getProxyCa(webTarget._id);
      const blob = new Blob([cert], { type: 'application/x-pem-file' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'zerberos-proxy-CA.crt';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Download failed: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleRegenerateCA = async () => {
    if (!webTarget?._id) return;
    const ok = window.confirm(
      'Regenerate the proxy CA?\n\nThis creates a BRAND-NEW certificate authority. The current one stops being trusted immediately, and EVERY browser must re-import the new CA before the proxy will work again.\n\nOnly do this if the CA is compromised or you deliberately want to rotate it.'
    );
    if (!ok) return;
    try {
      const res = await webTargetService.regenerateProxyCa(webTarget._id);
      setCaFingerprint(res.fingerprint || null);
      toast.warning('CA regenerated. Download it and re-import into every browser.', { autoClose: 8000 });
    } catch (err) {
      toast.error('Regenerate failed: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDeleteSession = async (sessionId) => {
    try {
      await proxyService.remove(sessionId);
      loadSessions();
    } catch (err) {
      console.error('Delete session error:', err);
    }
  };

  const handlePromoteAuth = async (captured) => {
    try {
      await authProfileService.promote({
        proxySessionId: activeSession._id,
        capturedAuthIndex: capturedAuth.indexOf(captured),
        name: `Captured ${captured.type} - ${new Date().toLocaleTimeString()}`,
        project: webTarget.project
      });
      onRefresh?.();
    } catch (err) {
      console.error('Promote error:', err);
    }
  };

  const inputClass = `w-full text-xs rounded border px-2 py-1.5 ${isDarkMode ? 'bg-gray-900 border-gray-600 text-gray-300' : 'bg-gray-50 border-gray-300 text-gray-700'}`;
  const labelClass = `text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`;
  const sectionHeader = (key, label) => (
    <button
      onClick={() => toggleSection(key)}
      className={`flex items-center gap-2 w-full text-left py-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}
    >
      <FontAwesomeIcon icon={expandedSections[key] ? faChevronDown : faChevronRight} className="text-[10px] w-3" />
      <span className="text-xs font-medium">{label}</span>
    </button>
  );

  return (
    <div className="p-4 space-y-4 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 150px)' }}>
      <div className="flex items-center gap-3">
        <FontAwesomeIcon icon={faShieldAlt} className="text-teal-400 text-lg" />
        <h3 className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Intercepting Proxy</h3>
        {activeSession?.status === 'running' && (
          <span className="px-2 py-0.5 rounded-full text-[10px] bg-green-900/30 text-green-400">
            Running on {config.listenAddress}:{activeSession.port || config.port}
            {config.routeViaAgent && config.selectedAgentId && (
              <span className="ml-1 text-teal-400">via agent</span>
            )}
          </span>
        )}
      </div>

      {/* Config */}
      <div className={`p-4 rounded-lg border space-y-3 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className={labelClass}>Listen Address</label>
            <select value={config.listenAddress} onChange={e => { setConfig(p => ({ ...p, listenAddress: e.target.value })); markDirty(); }}
              className={inputClass} disabled={!!activeSession}>
              <option value="127.0.0.1">127.0.0.1 (Local / SSH tunnel)</option>
              <option value="0.0.0.0">0.0.0.0 (All interfaces)</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>Proxy Port</label>
            <input type="number" value={config.port} onChange={e => { setConfig(p => ({ ...p, port: parseInt(e.target.value) })); markDirty(); }}
              className={inputClass} disabled={!!activeSession} />
          </div>
          <div className="flex items-end gap-3">
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={config.interceptEnabled}
                onChange={e => { setConfig(p => ({ ...p, interceptEnabled: e.target.checked })); markDirty(); }}
                className="rounded" disabled={!!activeSession} />
              <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Intercept</span>
            </label>
          </div>
        </div>

        {/* Decompress toggle */}
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <input type="checkbox" checked={config.decompressResponses}
            onChange={e => { setConfig(p => ({ ...p, decompressResponses: e.target.checked })); markDirty(); }}
            className="rounded" disabled={!!activeSession} />
          <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Decompress responses (gzip, deflate, brotli)</span>
        </label>


        <div>
          <div className="flex items-center justify-between">
            <label className={labelClass}>Scope Include (one pattern per line)</label>
            <label className="flex items-center gap-1.5 text-[11px] cursor-pointer" title="Passes and records ALL hosts (sets the include list to '*'). Nothing is blocked as out-of-scope.">
              <input type="checkbox" checked={allowAllHosts}
                onChange={e => toggleAllowAllHosts(e.target.checked)} className="rounded" />
              <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Allow all hosts</span>
            </label>
          </div>
          <textarea value={config.scopeInclude} onChange={e => { setConfig(p => ({ ...p, scopeInclude: e.target.value })); markDirty(); }}
            rows={2} className={`${inputClass} font-mono mt-1`} placeholder="*netsolution.ch  (use * for all hosts)" disabled={allowAllHosts} />
        </div>
        <div>
          <label className={labelClass}>Scope Exclude</label>
          <textarea value={config.scopeExclude} onChange={e => { setConfig(p => ({ ...p, scopeExclude: e.target.value })); markDirty(); }}
            rows={2} className={`${inputClass} font-mono mt-1`} placeholder="*.google.com" />
        </div>
        {activeSession && (
          <p className={`text-[10px] -mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            Scope changes apply live to the running proxy when you click Save — no restart needed.
          </p>
        )}

        {/* TLS Pass Through */}
        <div className={`pt-2 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          {sectionHeader('tls', 'TLS Pass Through')}
          {expandedSections.tls && (
            <div className="mt-2">
              <p className={`text-[10px] mb-1.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                Hostnames matching these patterns pass through without MITM interception (one per line)
              </p>
              <textarea value={config.tlsPassThrough} onChange={e => { setConfig(p => ({ ...p, tlsPassThrough: e.target.value })); markDirty(); }}
                rows={3} className={`${inputClass} font-mono`} placeholder="*.googleapis.com&#10;*.gstatic.com&#10;*.microsoft.com" disabled={!!activeSession} />
            </div>
          )}
        </div>

        {/* Don't Record Content Types */}
        <div className={`pt-2 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          {sectionHeader('contentFilter', 'Don\'t Record Content Types')}
          {expandedSections.contentFilter && (
            <div className="mt-2">
              <p className={`text-[10px] mb-1.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                Responses matching these content types won't be recorded (one per line, supports wildcards)
              </p>
              <textarea value={config.dontRecordContentTypes} onChange={e => { setConfig(p => ({ ...p, dontRecordContentTypes: e.target.value })); markDirty(); }}
                rows={4} className={`${inputClass} font-mono`} placeholder="image/*&#10;font/*&#10;text/css" disabled={!!activeSession} />
              <p className={`text-[10px] mt-2 mb-1.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                Don't record URLs matching these patterns (regex, one per line). Still proxied to the browser — just not stored. Good for noise like <code>/amb/connect</code>.
                {activeSession ? ' Applies live on Save.' : ''}
              </p>
              <textarea value={config.dontRecordUrlPatterns} onChange={e => { setConfig(p => ({ ...p, dontRecordUrlPatterns: e.target.value })); markDirty(); }}
                rows={3} className={`${inputClass} font-mono`} placeholder="/amb/connect&#10;/api/now/ui/presence&#10;xmlhttp\.do" />
            </div>
          )}
        </div>

        {/* Match & Replace */}
        <div className={`pt-2 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          {sectionHeader('matchReplace', `Match & Replace${config.matchReplaceRules.length ? ` (${config.matchReplaceRules.length})` : ''}`)}
          {expandedSections.matchReplace && (
            <div className="mt-2">
              <p className={`text-[10px] mb-1.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                Modify requests/responses as they pass through the proxy using regex patterns.
                {activeSession ? ' Changes apply live to the running proxy when you click Save.' : ''}
              </p>
              <MatchReplaceEditor
                rules={config.matchReplaceRules}
                onChange={rules => { setConfig(p => ({ ...p, matchReplaceRules: rules })); markDirty(); }}
                isDarkMode={isDarkMode}
              />
            </div>
          )}
        </div>

        {/* Response Modifications (Burp-style automatic transforms) */}
        <div className={`pt-2 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          {sectionHeader('responseMods', `Response Modification${responseModCount ? ` (${responseModCount})` : ''}`)}
          {expandedSections.responseMods && (
            <div className="mt-2">
              <p className={`text-[10px] mb-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                Automatically rewrite HTML responses as they pass through the proxy (Burp-style).
                {activeSession ? ' Changes apply live when you click Save.' : ''}
              </p>
              <div className="space-y-1">
                {RESPONSE_MOD_OPTIONS.map(opt => (
                  <label key={opt.key} className={`flex items-start gap-1.5 text-[11px] cursor-pointer ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} title={opt.hint}>
                    <input
                      type="checkbox"
                      checked={!!config.responseModifications?.[opt.key]}
                      onChange={e => { setConfig(p => ({ ...p, responseModifications: { ...p.responseModifications, [opt.key]: e.target.checked } })); markDirty(); }}
                      className="mt-0.5"
                    />
                    <span>
                      {opt.label}
                      <span className={`block text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{opt.hint}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Agent Routing */}
        <div className={`pt-2 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          {sectionHeader('agent', 'Agent Routing')}
          {expandedSections.agent && (
            <div className="mt-2 space-y-2">
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={config.routeViaAgent}
                  onChange={e => { setConfig(p => ({ ...p, routeViaAgent: e.target.checked })); markDirty(); }}
                  className="rounded" disabled={!!activeSession} />
                <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Route traffic via agent</span>
              </label>
              {config.routeViaAgent && (
                <div className="space-y-2 ml-5">
                  <div>
                    <label className={labelClass}>Agent</label>
                    <div className="flex gap-2 mt-1">
                      <select value={config.selectedAgentId || ''} onChange={e => { setConfig(p => ({ ...p, selectedAgentId: e.target.value || null })); markDirty(); }}
                        className={`flex-1 ${inputClass}`} disabled={!!activeSession}>
                        <option value="">-- Select agent --</option>
                        {onlineAgents.map(agent => (
                          <option key={agent._id} value={agent._id}>
                            {agent.name} - {agent.systemInfo?.hostname || agent.agentId} ({agent.platform})
                          </option>
                        ))}
                      </select>
                      <button onClick={loadOnlineAgents}
                        className={`px-2 rounded text-xs ${isDarkMode ? 'bg-gray-700 text-gray-400 hover:bg-gray-600' : 'bg-gray-200 text-gray-500 hover:bg-gray-300'}`}
                        title="Refresh agents">
                        <FontAwesomeIcon icon={faSync} />
                      </button>
                    </div>
                    {onlineAgents.length === 0 && (
                      <p className="text-[10px] text-yellow-500 mt-1">No agents currently online</p>
                    )}
                  </div>
                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <input type="checkbox" checked={config.agentTlsSkipVerify}
                      onChange={e => { setConfig(p => ({ ...p, agentTlsSkipVerify: e.target.checked })); markDirty(); }}
                      className="rounded" disabled={!!activeSession} />
                    <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Skip TLS verification on agent</span>
                  </label>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Unified Save Button */}
        {settingsDirty && (
          <button onClick={handleSaveSettings} disabled={settingsSaving}
            className="px-3 py-1.5 bg-teal-600 text-white rounded text-xs font-medium hover:bg-teal-700 disabled:opacity-50 flex items-center gap-1.5">
            {settingsSaving ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faCheckCircle} />} Save Settings
          </button>
        )}
      </div>

      {/* Controls */}
      <div className="flex gap-2">
        {!activeSession ? (
          <button onClick={handleStart} disabled={loading}
            className="px-4 py-2 bg-teal-600 text-white rounded text-xs font-medium hover:bg-teal-700 disabled:opacity-50 flex items-center gap-1.5">
            {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faPlay} />} Start Proxy
          </button>
        ) : (
          <>
            <button onClick={handleStop} disabled={loading}
              className="px-4 py-2 bg-red-600 text-white rounded text-xs font-medium hover:bg-red-700 disabled:opacity-50 flex items-center gap-1.5">
              {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faStop} />} Stop
            </button>
            <button onClick={handleRestart} disabled={loading}
              className="px-4 py-2 bg-yellow-600 text-white rounded text-xs font-medium hover:bg-yellow-700 disabled:opacity-50 flex items-center gap-1.5">
              {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faRedo} />} Restart
            </button>
            <button onClick={handleDownloadCA}
              className={`px-4 py-2 rounded text-xs flex items-center gap-1.5 ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>
              <FontAwesomeIcon icon={faDownload} /> CA Certificate
            </button>
          </>
        )}
        <button onClick={() => { loadSessions(); onRefresh?.(); }}
          className={`px-4 py-2 rounded text-xs ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>
          <FontAwesomeIcon icon={faSync} className="mr-1" /> Refresh
        </button>
      </div>

      {/* Proxy CA — fixed per target; only rotates via explicit Regenerate */}
      <div className={`mt-2 flex flex-wrap items-center gap-2 text-xs rounded border px-3 py-2 ${isDarkMode ? 'bg-gray-900/40 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
        <FontAwesomeIcon icon={faShieldAlt} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
        <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Proxy CA</span>
        {caFingerprint ? (
          <span className={`font-mono text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} title={caFingerprint}>
            {caFingerprint.slice(0, 16)}…
          </span>
        ) : (
          <span className={isDarkMode ? 'text-gray-600' : 'text-gray-400'}>not yet created (start proxy once)</span>
        )}
        <span className={`ml-auto ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>Fixed — import once; never auto-rotates.</span>
        <button onClick={handleDownloadCA}
          className={`px-3 py-1 rounded flex items-center gap-1.5 ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>
          <FontAwesomeIcon icon={faDownload} /> Download CA
        </button>
        <button onClick={handleRegenerateCA}
          className="px-3 py-1 rounded flex items-center gap-1.5 bg-red-600/80 text-white hover:bg-red-600"
          title="Rotate the CA — breaks trust until every browser re-imports the new one">
          <FontAwesomeIcon icon={faRedo} /> Regenerate
        </button>
      </div>

      {/* Cookie Manager */}
      {activeSession && (
        <ProxyCookieManager
          key={`cookies-${activeSession._id}-${cookieRefreshTrigger}`}
          sessionId={activeSession._id}
          onCookiesChange={(cookies) => {
            // Optionally handle cookie changes
            console.log('Cookies updated:', cookies);
          }}
        />
      )}

      {/* Cloudflare Auto-Solver */}
      {activeSession && (
        <div className="mt-3">
          <CloudflareSolver
            sessionId={activeSession._id}
            webTarget={webTarget}
            isDarkMode={isDarkMode}
            onCookiesImported={(cookies) => {
              // Trigger Cookie Manager refresh
              setCookieRefreshTrigger(prev => prev + 1);
            }}
          />
        </div>
      )}

      {/* Captured Auth */}
      {capturedAuth.length > 0 && (
        <div className={`p-3 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <h4 className={`text-xs font-medium mb-2 flex items-center gap-1.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            <FontAwesomeIcon icon={faKey} className="text-yellow-400" /> Captured Authentication ({capturedAuth.length})
          </h4>
          <div className="space-y-1.5">
            {capturedAuth.map((auth, i) => (
              <div key={i} className={`flex items-center justify-between px-2 py-1.5 rounded text-xs ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
                <div className="flex items-center gap-2">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-mono ${isDarkMode ? 'bg-yellow-900/30 text-yellow-300' : 'bg-yellow-100 text-yellow-700'}`}>
                    {auth.type}
                  </span>
                  <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>{auth.host || 'unknown'}</span>
                  {auth.decoded?.sub && <span className={`text-[10px] ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>sub: {auth.decoded.sub}</span>}
                </div>
                <button onClick={() => handlePromoteAuth(auth)}
                  className="text-[10px] text-teal-400 hover:text-teal-300">
                  Promote to Auth Profile
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Session History */}
      {sessions.length > 0 && (
        <div className={`p-3 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <h4 className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Session History</h4>
          <div className="space-y-1">
            {sessions.slice(0, 10).map(s => (
              <div key={s._id} className={`flex items-center justify-between text-[10px] px-2 py-1 rounded ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
                <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                  Port {s.proxyConfig?.listenPort || s.port || '?'} - {s.stats?.totalRequests || 0} requests
                </span>
                <div className="flex items-center gap-2">
                  <span className={s.status === 'running' ? 'text-green-400' : (isDarkMode ? 'text-gray-600' : 'text-gray-400')}>
                    {s.status}
                  </span>
                  {s.status !== 'running' && (
                    <button onClick={() => handleDeleteSession(s._id)}
                      className="text-red-500/60 hover:text-red-400 transition-colors" title="Delete session">
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProxyControlPanel;
